/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router";
import { identify, reset, track, EVENTS } from "@/lib/analytics";
import {
  createClient,
  Session,
  User as SupabaseUser,
} from "@supabase/supabase-js";
import { ServerAuthResponse, User } from "@/types/auth";
import { authService } from "@/api/service";
import { AuthContext } from "./AuthContext";
import { ApiResponse } from "@/types/apiResponse";

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Local storage keys
const USER_STORAGE_KEY = "gt-user";
const USER_ACCESS_TOKEN = "gt-access-token";

const extractUserData = (supabaseUser: SupabaseUser): User => ({
  id: supabaseUser.id,
  email: supabaseUser.email || "",
  name:
    supabaseUser.user_metadata?.full_name ||
    supabaseUser.email?.split("@")[0] ||
    "User",
  picture: supabaseUser.user_metadata?.avatar_url || "",
});

const saveUserToStorage = (userData: User) => {
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
};

const getUserFromStorage = (): User | null => {
  try {
    const storedUser = localStorage.getItem(USER_STORAGE_KEY);
    return storedUser ? JSON.parse(storedUser) : null;
  } catch {
    return null;
  }
};

const clearUserFromStorage = () => {
  localStorage.removeItem(USER_STORAGE_KEY);
  localStorage.removeItem(USER_ACCESS_TOKEN);
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(getUserFromStorage);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const navigate = useNavigate();
  const sessionCheckDone = useRef(false);

  const handleTokenExchange = useCallback(
    async (session: Session): Promise<User | null> => {
      const accessToken = session.access_token;
      setTokenLoading(true);
      try {
        const response: ApiResponse<ServerAuthResponse> =
          await authService.getServerToken(accessToken);

        if (response.success && response.data?.user) {
          localStorage.setItem(USER_ACCESS_TOKEN, response.data.token);
          return {
            id: response.data.user.id,
            email: response.data.user.email,
            name: response.data.user.name,
            picture: response.data.user.picture,
          };
        } else {
          console.warn("Backend token exchange failed:", response.message);
          return null;
        }
      } finally {
        setTokenLoading(false);
      }
    },
    []
  );

  // Initial session check - runs once
  useEffect(() => {
    if (sessionCheckDone.current) return;
    sessionCheckDone.current = true;

    const checkSession = async () => {
      setIsLoading(true);
      setLoadingMessage("Checking your session...");
      setError(null);

      try {
        const storedUser = getUserFromStorage();
        if (storedUser) {
          setUser(storedUser);
          identify(storedUser.id, { email: storedUser.email, name: storedUser.name });
          setIsLoading(false);
          setLoadingMessage(null);

          // Validate session in the background
          const { data, error: sessionError } =
            await supabase.auth.getSession();
          if (sessionError || !data.session) {
            setUser(null);
            clearUserFromStorage();
            setError("Session expired. Please sign in again.");
          }
          return;
        }

        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          setError(sessionError.message);
          return;
        }

        if (data?.session) {
          setLoadingMessage("Logging you in...");
          const userData = await handleTokenExchange(data.session);
          const finalUserData = userData || extractUserData(data.session.user);
          setUser(finalUserData);
          saveUserToStorage(finalUserData);
          identify(finalUserData.id, { email: finalUserData.email, name: finalUserData.name });
          track(EVENTS.SIGNED_IN, { method: "google" });
        }
      } catch (error) {
        console.error("Session check failed:", error);
        setError(error instanceof Error ? error.message : "Unknown error");
      } finally {
        setIsLoading(false);
        setLoadingMessage(null);
      }
    };

    checkSession();
  }, [handleTokenExchange]);

  // Supabase auth listener
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session) {
        const local = getUserFromStorage();
        const server = extractUserData(session.user);
        if (!local || local.id !== server.id) {
          setIsLoading(true);
          try {
            const userData = await handleTokenExchange(session);
            const finalUserData = userData || server;

            setUser((prev) => {
              if (JSON.stringify(prev) !== JSON.stringify(finalUserData)) {
                saveUserToStorage(finalUserData);
                return finalUserData;
              }
              return prev;
            });
          } catch (err) {
            console.error("Sign in error:", err);
          } finally {
            setIsLoading(false);
          }
        }
      }

      if (event === "SIGNED_OUT") {
        setUser(null);
        clearUserFromStorage();
      }

      if (event === "TOKEN_REFRESHED" && session) {
        console.log("Token refreshed");
      }
    });

    return () => subscription.unsubscribe();
  }, [handleTokenExchange]);

  const signInWithGoogle = useCallback(async () => {
    setIsLoading(true);
    setLoadingMessage("Redirecting to Google...");
    setError(null);

    try {
      const { error: signInError } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: window.location.href },
      });

      if (signInError) setError(signInError.message);
    } catch (error) {
      console.error("Google sign in failed:", error);
      setError(
        error instanceof Error ? error.message : "Google sign in failed"
      );
    } finally {
      setIsLoading(false);
      setLoadingMessage(null);
    }
  }, []);

  const signIn = useCallback(
    (response: any) => {
      try {
        const userData: User = {
          email: response.email,
          name: response.name,
          picture: response.picture,
          id: response.sub,
        };
        setUser(userData);
        saveUserToStorage(userData);
        identify(userData.id, { email: userData.email, name: userData.name });
        track(EVENTS.SIGNED_IN, { method: "manual" });
      } catch (error) {
        console.error("Manual sign in error:", error);
        setError(
          error instanceof Error ? error.message : "Failed to sign in"
        );
      }
    },
    []
  );

  const signOut = useCallback(async () => {
    setIsLoading(true);
    setLoadingMessage("Signing you out...");
    setError(null);

    try {
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) {
        setError(signOutError.message);
        setIsLoading(false);
        setLoadingMessage(null);
        return;
      }

      setUser(null);
      clearUserFromStorage();
      reset();

      setTimeout(() => {
        navigate("/");
        setIsLoading(false);
        setLoadingMessage(null);
      }, 1000);
    } catch (error) {
      console.error("Sign out failed:", error);
      setError(error instanceof Error ? error.message : "Failed to sign out");
      setIsLoading(false);
      setLoadingMessage(null);
    }
  }, [navigate]);

  const isLoadingCombined = isLoading || tokenLoading;

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading: isLoadingCombined,
      error,
      signIn,
      signOut,
      signInWithGoogle,
      clearError: () => setError(null),
      loadingMessage,
    }),
    [user, isLoadingCombined, error, signIn, signOut, signInWithGoogle, loadingMessage]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
